import { useState, useEffect } from "react";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const categories = ["Tout", "cérémonie", "portrait", "réception", "demoiselle d'honneur", "Fleurs & Déco"];

export default function Gallery() {
  useScrollReveal();
  const [photos, setPhotos] = useState([]);
  const [activeCategory, setActiveCategory] = useState("Tout");
  const [lightbox, setLightbox] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/gallery`)
      .then(r => r.json())
      .then(data => { setPhotos(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const filtered = activeCategory === "Tout"
    ? photos
    : photos.filter(p => p.category === activeCategory);

  return (
    <main className="page-fade" data-testid="gallery-page" style={{ paddingTop: "72px" }}>

      {/* Header */}
      <section style={{ padding: "100px 24px 80px", backgroundColor: "#F7F3EA", textAlign: "center" }}>
        <p style={{ fontSize: "10px", letterSpacing: "5px", textTransform: "uppercase", color: "rgba(61,22,2,0.4)", marginBottom: "20px" }}>
          Galerie & Inspirations
        </p>
        <h1 style={{
          fontFamily: "'Noto Serif Ethiopic', Georgia, serif",
          fontSize: "clamp(2.5rem, 6vw, 5rem)",
          fontWeight: 300, color: "#3D1602",
          letterSpacing: "-0.02em", lineHeight: 1.1, marginBottom: "20px",
        }}>
          Des mariages orchestrés
        </h1>
        <p style={{ fontFamily: "'Noto Serif Ethiopic', Georgia, serif", fontStyle: "italic", fontSize: "22px", color: "rgba(61,22,2,0.5)" }}>
          Chaque image raconte une histoire.
        </p>
      </section>

      {/* Filter */}
      <section style={{ padding: "40px 0 0", backgroundColor: "#F7F3EA" }}>
        <div style={{ maxWidth: "1400px", margin: "0 auto", overflowX: "auto", WebkitOverflowScrolling: "touch", padding: "0 20px 8px" }}>
          <div style={{ display: "flex", gap: "12px", justifyContent: "center", whiteSpace: "nowrap", minWidth: "max-content", margin: "0 auto" }}>
          {categories.map((cat) => (
            <button
              key={cat}
              data-testid={`filter-${cat}`}
              onClick={() => setActiveCategory(cat)}
              style={{
                fontSize: "10px",
                letterSpacing: "3px",
                textTransform: "uppercase",
                fontFamily: "'Inter', sans-serif",
                padding: "8px 20px",
                border: "1px solid",
                borderColor: activeCategory === cat ? "#3D1602" : "rgba(61,22,2,0.2)",
                backgroundColor: activeCategory === cat ? "#3D1602" : "transparent",
                color: activeCategory === cat ? "#F7F3EA" : "#6D5E56",
                cursor: "pointer",
                transition: "all 0.3s ease",
              }}
            >
              {cat}
            </button>
          ))}
          </div>
        </div>
      </section>
      <section style={{ padding: "48px 24px 100px", backgroundColor: "#F7F3EA" }}>
        <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: "80px", color: "#6D5E56" }}>
              <p style={{ fontFamily: "'Noto Serif Ethiopic', Georgia, serif", fontStyle: "italic", fontSize: "24px" }}>Chargement...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: "center", padding: "80px", color: "#6D5E56" }}>
              <p>Aucune photo dans cette catégorie.</p>
            </div>
          ) : (
            <div className="gallery-masonry">
              {filtered.map((photo, i) => (
                <div
                  key={photo.id}
                  className="gallery-item img-hover"
                  data-testid={`gallery-photo-${i}`}
                  onClick={() => setLightbox(photo)}
                  style={{ cursor: "pointer", position: "relative" }}
                >
                  <img
                    src={photo.url}
                    alt={photo.title}
                    style={{ width: "100%", display: "block" }}
                    loading="lazy"
                  />
                  <div style={{
                    position: "absolute", inset: 0,
                    backgroundColor: "rgba(61,22,2,0)",
                    transition: "background-color 0.4s ease",
                    display: "flex", alignItems: "flex-end",
                  }}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = "rgba(61,22,2,0.35)"; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = "rgba(61,22,2,0)"; }}
                  >
                    <div style={{ padding: "16px", opacity: 0 }}
                      className="gallery-overlay-text">
                      <p style={{ color: "#3D1602", fontSize: "12px", letterSpacing: "2px" }}>{photo.title}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && (
        <div
          data-testid="lightbox"
          onClick={() => setLightbox(null)}
          style={{
            position: "fixed", inset: 0, zIndex: 200,
            backgroundColor: "rgba(0,0,0,0.92)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "24px",
          }}
        >
          <button
            data-testid="lightbox-close"
            onClick={() => setLightbox(null)}
            style={{
              position: "absolute", top: "24px", right: "24px",
              background: "none", border: "none", cursor: "pointer",
              color: "rgba(247,243,234,0.7)", padding: "8px"
            }}
          >
            <X size={28} />
          </button>
          <div onClick={e => e.stopPropagation()} style={{ maxWidth: "90vw", maxHeight: "90vh" }}>
            <img
              src={lightbox.url}
              alt={lightbox.title}
              style={{ maxWidth: "100%", maxHeight: "80vh", objectFit: "contain", display: "block" }}
            />
            <p style={{
              textAlign: "center", marginTop: "16px",
              fontSize: "12px", letterSpacing: "3px", textTransform: "uppercase",
              color: "rgba(61,22,2,0.5)", fontFamily: "'Inter', sans-serif",
            }}>
              {lightbox.title}
            </p>
          </div>
        </div>
      )}

      {/* Instagram CTA */}
      <section style={{ padding: "80px 24px", backgroundColor: "#F7F3EA", textAlign: "center" }}>
        <p className="scroll-reveal" style={{ fontFamily: "'Noto Serif Ethiopic', Georgia, serif", fontStyle: "italic", fontSize: "26px", color: "rgba(61,22,2,0.6)", marginBottom: "16px" }}>
          Pour plus d'inspirations
        </p>
        <h2 className="scroll-reveal delay-1" style={{
          fontFamily: "'Noto Serif Ethiopic', Georgia, serif",
          fontSize: "clamp(1.5rem, 3vw, 2rem)",
          fontWeight: 300, color: "#3D1602", marginBottom: "32px",
        }}>
          Suivez-nous sur Instagram
        </h2>
        <div className="scroll-reveal delay-2">
          <a
            href="https://www.instagram.com/ellaa.wedding/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="gallery-instagram"
            className="btn-outline-dark"
          >
            @ellaa.wedding
          </a>
        </div>
      </section>
    </main>
  );
}
